import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { LogoLoading } from '@/components/ui/LogoLoading';
import { ClosedCompany } from '@/pages/ClosedCompany';
import { api } from '@/lib/api';

type OrgResponse = {
  organization: { deletion?: { purge_at: string | null } | null };
};

export function RequireAuth() {
  const { isAuthenticated, isBootstrapping } = useAuth();
  const location = useLocation();

  /*
    A COMPANY BEING DELETED IS SHUT, AND THAT REPLACES THE WHOLE APP.

    Asked here rather than painted as a banner over a working dashboard,
    because the server answers 423 to every other endpoint the moment deletion
    is requested. A dashboard whose every button fails is a broken product; one
    screen saying the company is closed, with the way back on it, is an honest
    one.

    `/org` is deliberately one of the three calls the lock lets through, so this
    query works while everything around it is refused. It is the same cache key
    the rest of the app uses, so it costs no extra request on a normal day.

    `enabled` on being signed in: an anonymous visitor asking /org gets a 401
    and a redirect loop.
  */
  const { data, isPending } = useQuery<OrgResponse>({
    queryKey: ['org'],
    queryFn: () => api<OrgResponse>('/org'),
    enabled: isAuthenticated && !isBootstrapping,
    staleTime: 60_000,
  });

  if (isBootstrapping) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <LogoLoading size={96} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /* Nothing is rendered while the answer is unknown. Showing the dashboard for
     a moment and then replacing it with "this company is closed" is worse than
     the spinner that is already this app's loading state. */
  if (isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <LogoLoading size={96} />
      </div>
    );
  }

  if (data?.organization.deletion?.purge_at) return <ClosedCompany />;

  return <Outlet />;
}
